import {
  inject,
  ref,
  computed,
  provide,
  InjectionKey,
  onMounted,
  watch,
  Ref,
  watchEffect,
  onBeforeMount
} from 'vue';
import { subscribeKey, unsubscribeKey } from '@drapejs/core';

import {
  commands,
  getProductSectionsCacheKey
} from '@/connectors/litium/giftCategory';

import useContext from '@/composables/useContext';
import useExecuteCommand from '@/composables/useExecuteCommand';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useGiftCard from '@/composables/useGiftCard';

const isLoadingGiftProductsKey: InjectionKey<Ref<Boolean>> = Symbol(
  'isLoadingGiftProducts'
);
const productSectionsKey: InjectionKey<Ref<any>> = Symbol('productSections');
const singleSectionKey: InjectionKey<Ref<Boolean>> = Symbol('singleSection');
const loadAllSectionProductsKey: InjectionKey<
  ({ categoryId, sectionId }: any) => {}
> = Symbol('loadAllSectionProducts');

export default function () {
  const { route, page } = useContext();
  const { executeCommand } = useExecuteCommand();
  const { websiteText } = useWebsiteTexts();
  const { activeGiftCard } = useGiftCard();

  let cacheKey;

  type SubscribeFn = (
    key: string,
    handler: (val: any) => void,
    options?: { immediate?: boolean }
  ) => void;
 
  type UnsubscribeFn = (key: string, handler: (val: any) => void) => void;
 
  const cache = {
    subscribe: inject<SubscribeFn>(subscribeKey, () => {}),
    unsubscribe: inject<UnsubscribeFn>(unsubscribeKey, () => {})
  };

  const categoryData = ref<any>();

  const isLoadingProducts = ref(false);
  provide(isLoadingGiftProductsKey, isLoadingProducts);

  const productSections = computed(() => {
    let sections = [...(categoryData.value?.productSections || [])];
    upsertActiveSectionIfAny(route.query.section, sections);
    return sections;
  });
  provide(productSectionsKey, productSections);

  const singleSection = computed(() => categoryData?.value?.singleSection);

  onMounted(() => {
    watch(
      () => activeGiftCard.value,
      (val) => {
        if (!val?.firstCategory?.categoryId) {
          return;
        }

        if (cacheKey) {
          cache.unsubscribe(cacheKey, cacheHandler);
        }

        cacheKey = getProductSectionsCacheKey(
          route.pathname,
          activeGiftCard.value?.firstCategory?.categoryId
        );
        cache.subscribe(cacheKey, cacheHandler, {
          immediate: true
        });
      },
      {
        immediate: true
      }
    );
  });

  onBeforeMount(() => {
    if (cacheKey) {
      cache.unsubscribe(cacheKey, cacheHandler);
    }
  });

  function cacheHandler(val: any) {
    isLoadingProducts.value = false;
    categoryData.value = val?.categoryGiftsV2;
  }

  provide(singleSectionKey, singleSection);

  provide(
    loadAllSectionProductsKey,
    async ({ categorySystemId, section }: any) => {
      try {
        isLoadingProducts.value = true;
        const response = await executeCommand(commands.loadAllSectionProducts, {
          categorySystemId,
          section
        });
        if (response.error) {
          throw response.error;
        }
      } catch (err) {
        console.error(err);
        throw websiteText('category__load_all_products_error').value;
      } finally {
        isLoadingProducts.value = false;
      }
    }
  );

  return {
    isLoadingProducts,
    productSections,
    singleSection
  };
}

export function getContext() {
  return {
    isLoadingGiftProducts: inject(
      isLoadingGiftProductsKey,
      ref<Boolean>(false)
    ),
    productSections: inject(productSectionsKey, ref<any>(<any>[])),
    loadAllSectionProducts: inject(
      loadAllSectionProductsKey,
      ({ categoryId, sectionId }: any) => {}
    ),
    isSingleSection: inject(singleSectionKey, ref<Boolean>(false))
  };
}

export function generateHtmlElementId(val: string) {
  return `#${decodeURIComponent(val || '')
    .replace(/\W/g, '')
    .toLowerCase()}`;
}

function upsertActiveSectionIfAny(section: any, sections: any[]) {
  const sectionValue = getSectionValue(section);
  if (sectionValue) {
    const sectionIdx = sections.findIndex(
      (s) => s?.tags?.[0]?.value == sectionValue
    );
    if (sectionIdx != -1) {
      const section = sections.splice(sectionIdx, 1)?.[0];
      if (section) {
        sections.unshift(section);
      }
    }
  }

  function getSectionValue(section: any) {
    try {
      return section?.substring(section?.indexOf('::') + 2);
    } catch {
      return '';
    }
  }
}
